/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';
import ClientSideConfigSelector from '../../../../../recoil/cashout/ClientSideConfigSelector';
import { getProductFormattedNumber } from '../../../../helpers/CurrencyUtils';
import CashoutEmailModalContent from '../CashoutEmailModalContent';
import Diamond from '../../../../icons/Diamond';
import CashoutOnboardingForm from '../../forms/CashoutOnboardingForm';
import CashoutAwards from '../../common/CashoutAwards';
import { summarize } from '../../../../helpers/ProductUtils';

export default function CashoutConfirmation({
  payAccount, payMethod, selectedPackage,
  onEmailChanged, onCountryCodeChanged, onPaymentMethodUpdate, onSubmit, onClose,
}) {
  const { t } = useTranslation(null, { useSuspense: false });
  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
        providers: {
          [paymentProvider]: {
            display,
            providerLegalPage,
          },
        },
        isOpeningLinksInNewWindowSupported = true,
      } = {},
    } = {},
  } = config || {};

  const {
    email,
  } = payAccount;

  const {
    contactFirstName,
    contactLastName,
    paymentMethodType,
    paymentMethodCurrency,
    paymentMethodAccountType,
    paymentMethodAccountNumber,
    paymentMethodAccountIban,
  } = payMethod || {};

  const [showUpdateEmail, setShowUpdateEmail] = useState(false);
  const [showUpdatePaymentMethod, setShowUpdatePaymentMethod] = useState(false);

  const isProviderWire = paymentProvider === 'wire'; // todo: for backward compatibility, remove after fully switching to tipalti

  const destinationContent = useMemo(() => {
    if (paymentProvider === 'wire') {
      return (
        <p className="col-xs-12 cash-out-text">
          {t('cashout_wire_confirmation_message')}
        </p>
      );
    }

    if (paymentProvider === 'tipalti' && paymentMethodType === 'WIRE_TRANSFER') {
      return (
        <p className="col-xs-12 cash-out-text">
          Wire Transfer (
          {paymentMethodCurrency}
          ) to
          {' '}
          {contactFirstName}
          {' '}
          {contactLastName}
        </p>
      );
    }

    if (paymentProvider === 'tipalti' && paymentMethodType === 'ACH') {
      return (
        <p className="col-xs-12 cash-out-text">
          {paymentMethodAccountType}
          {' '}
          {paymentMethodAccountNumber}
        </p>
      );
    }

    if (paymentProvider === 'tipalti' && paymentMethodType === 'E_CHECK') {
      return (
        <p className="col-xs-12 cash-out-text">
          IBAN
          {' '}
          {paymentMethodAccountIban}
        </p>
      );
    }

    if (paymentProvider === 'paypal') {
      return (
        <p className="col-xs-12 cash-out-text">
          {t('cashout_to_provider', { provider: display })}
          :&nbsp;
          <span id="cashout-confirmation-email">{email}</span>
        </p>
      );
    }

    return null;
    // eslint-disable-next-line
  }, [payAccount, display]);

  const updatePaymentMethodContent = useMemo(() => {
    if (paymentProvider === 'wire') {
      return (
        <div className="col-xs-12 cashout-update-email-button-container">
          <a href="/tax-faq">{t('cashout_tax_faq')}</a>
        </div>
      );
    }

    if (paymentProvider === 'tipalti') {
      return (
        <div className="col-xs-12 cashout-update-email-button-container">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowUpdatePaymentMethod(true);
            }}
          >
            {t('cashout_update_payment_method', { provider: display })}
          </a>
        </div>
      );
    }

    if (paymentProvider === 'paypal') {
      return (
        <div className="col-xs-12 cashout-update-email-button-container">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowUpdateEmail(true);
            }}
          >
            {t('cashout_update_provider_email', { provider: display })}
          </a>
        </div>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payAccount, display]);

  if (showUpdateEmail) {
    return (
      <CashoutEmailModalContent
        onSubmit={(newEmail, newCountryCode) => {
          onEmailChanged(newEmail);
          onCountryCodeChanged(newCountryCode);
          setShowUpdateEmail(false);
        }}
        onClose={() => {
          setShowUpdateEmail(false);
          onClose();
        }}
      />
    );
  }

  if (showUpdatePaymentMethod) {
    return (
      <CashoutOnboardingForm
        title={t('cashout_update_payment_method')}
        payAccount={payAccount}
        onClose={async () => {
          setShowUpdatePaymentMethod(false);
          await onPaymentMethodUpdate();
        }}
      />
    );
  }

  const { awards } = selectedPackage;
  const isMultipleAwards = (awards || []).length > 1;

  return (
    <div>
      <div
        id="cashout-confirmation"
        className="phoenix-simple-dialog-view modal member-diamonds-cash-confirm show"
        aria-hidden="false"
      >
        <div className="modal-backdrop fade in" />
        <div className="modal-dialog">
          <div className="modal-content modal-content-sm">
            <a
              className="close phoenix-icon phoenix-icon-cross hidden-xs hidden-sm"
              onClick={() => { onClose?.(); }}
              aria-hidden="true"
            />
            <div>
              <div className="modal-header">
                <h4>{t('cashout_confirm_request')}</h4>
                <hr className="below-header-breakline" />
              </div>
              <form
                id="cashout-confirmation-form"
                className="form-horizontal"
                onSubmit={onSubmit}
              >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-xs-12 cash-reward">
                      <p className="reward-message">
                        {t('cashout_cash_out')}
                        <span>
                          <span className="cashout-diamond-icon">
                            <Diamond />
                          </span>
                        </span>
                        {' '}
                        <span id="cashout-confirmation-diamonds">
                          {getProductFormattedNumber(summarize(selectedPackage.charges))}
                        </span>
                        {!isMultipleAwards ? ' ' : null}
                        <span className={`cashout-reward-for-awards ${isMultipleAwards ? 'cashout-reward-for-awards-multiline' : ''}`}>
                          {t('cashout_for')}
                          {' '}
                          <span id="cashout-confirmation-amount">
                            <span className="cashout-awards">
                              <CashoutAwards awards={awards} />
                            </span>
                          </span>
                        </span>
                      </p>
                    </div>

                    { destinationContent }

                    <hr className="below-email-breakline" />
                    <p className="col-xs-12 cash-out-text">
                      {t('cashout_submit_your_cash_out_request_')}
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="form-group">
                    <div className="col-xs-12">
                      <button
                        type="button"
                        className="btn btn-undesired btn-half"
                        data-dismiss="modal"
                        aria-hidden="true"
                        onClick={() => {
                          onClose?.();
                        }}
                      >
                        {t('cashout_cancel')}
                      </button>
                      <button type="submit" className="btn btn-primary btn-half" aria-hidden="true">{t('cashout_submit')}</button>

                      { updatePaymentMethodContent }
                    </div>
                  </div>
                </div>
                <div className="modal-footnote provider-content">
                  <p className="text-center">
                    {
                      isProviderWire
                        // todo: remove this block after fully switching to tipalti
                        ? (t('cash_to_provider_footnote_short', { provider: display }))
                        : (t('cashout_to_provider_footnote', { provider: display }))
                    }

                    {providerLegalPage && (
                      <>
                        {t('cashout_provider_legal_confirmation_message')}
                        {' '}
                        <a
                          href={providerLegalPage}
                          target={isOpeningLinksInNewWindowSupported ? '_blank' : undefined}
                          rel={isOpeningLinksInNewWindowSupported ? 'noopener noreferrer' : undefined}
                        >
                          {t('cashout_provider_legal_confirmation_message_footer')}
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
CashoutConfirmation.propTypes = {
  payAccount: PropTypes.object.isRequired,
  payMethod: PropTypes.object,
  selectedPackage: PropTypes.object.isRequired,
  onEmailChanged: PropTypes.func.isRequired,
  onCountryCodeChanged: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPaymentMethodUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
CashoutConfirmation.defaultProps = {
  payMethod: {},
};
